import { useEffect, useState } from 'react';
import { Btn, Field, useToast } from '@/components/ui';
import { adminUpdateProject } from '@/api/projects';
import { getErrorMessage } from '@/api';
import type { Project } from '@/api/types';
import styles from './InfoTab.module.css';

export const STAGE_OPTIONS = [{ value: '', label: '선택해 주세요' },
  ...[1, 2, 3, 4, 5].map(n => ({ value: `STAGE_${n}`, label: `Stage ${n}` })),
  { value: 'UA_OPS', label: 'UA Ops' }];

// 원장 ON에서만 표시한다. Stage는 UA 수집 여부와 별개이며 공통 변경은 모든 OS에 적용한다.
// 관리자는 여기서 바로 저장하고, 그 외에는 onRequestChange 로 값을 올려 '변경 사항'의
// 변경 요청에 함께 담는다(승인 후 반영).
export default function ProjectStageEditor({ project, canEdit, onSaved, requestValue, onRequestChange, ver = 0 }: {
  project: Project; canEdit: boolean; onSaved?: () => void;
  requestValue?: string; onRequestChange?: (stage: string) => void; ver?: number;
}) {
  const initial = project.stage ?? '';
  const [stage, setStage] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();
  useEffect(() => { setStage(initial); setError(''); }, [initial, project.project_id]);
  if (onRequestChange) {
    return <section className={styles.stageEditor} aria-label="게임 Stage">
      <Field key={`${project.project_id}-${initial}-${ver}`} label="Stage" lockable
        value={requestValue ?? initial} onChange={onRequestChange} readonly={!canEdit}
        help="바꾸면 아래 '변경 사항'에 담겨 변경 요청으로 보내집니다. 승인되면 모든 OS의 PSL·분석 Stage와 Stage별 조회 권한에 적용됩니다."
        selectOptions={initial ? STAGE_OPTIONS.filter(o => o.value) : STAGE_OPTIONS} />
    </section>;
  }
  const save = async () => {
    if (!project.project_id || !stage) return;
    setSaving(true);
    setError('');
    try {
      await adminUpdateProject(project.project_id, { common: { stage } });
      toast.success('모든 OS의 Stage를 저장하고 BigQuery에 반영했습니다. PSL 조회도 새 Stage를 사용합니다.');
      onSaved?.();
    } catch (err) {
      // 원장 저장 뒤 전달 실패는 성공으로 표시하지 않는다. 같은 값으로 재시도할 수 있다.
      setError(getErrorMessage(err));
    } finally { setSaving(false); }
  };
  return <section className={styles.stageEditor} aria-label="게임 Stage">
    <Field key={`${project.project_id}-${initial}`} label="Stage" lockable
      value={stage} onChange={setStage} readonly={!canEdit || saving}
      help="모든 OS의 PSL·분석 Stage와 Stage별 조회 권한에 적용됩니다. 개별 게임 권한과 UA 수집 상태는 유지됩니다."
      selectOptions={STAGE_OPTIONS} />
    {error && <p className={styles.stageError} role="alert">{error}</p>}
    {canEdit && (stage !== initial || error || saving) && <Btn disabled={saving || !stage} onClick={save}>
      {saving ? '반영 중' : '모든 OS에 Stage 적용'}
    </Btn>}
  </section>;
}
